(() => {
  if (window.WNCart) return;

  const copy = window.WN_I18N?.cart || {};
  const root = window.Shopify?.routes?.root || '/';
  const routes = {
    cart: `${root}cart.js`,
    add: `${root}cart/add.js`,
    change: `${root}cart/change.js`,
    update: `${root}cart/update.js`
  };

  let cartState = null;
  let refreshPromise = null;
  let mutationQueue = Promise.resolve();
  let queuedMutations = 0;

  function countLabel(container, count) {
    const template = container.dataset.labelTemplate || '[count]';
    return template.replace('[count]', String(count));
  }

  function renderCartCount(cart) {
    const count = Math.max(0, Number(cart?.item_count) || 0);

    document.querySelectorAll('[data-cart-count-container]').forEach((container) => {
      const bubble = container.querySelector('[data-cart-count]');
      const value = container.querySelector('[data-cart-count-value]');
      const link = container.querySelector('[data-cart-link]');
      const label = countLabel(container, count);
      const baseLabel = link?.dataset.baseLabel || '';

      if (value) value.textContent = String(count);

      if (bubble) {
        bubble.hidden = count < 1;
        bubble.setAttribute('aria-label', label);
      }

      if (link && baseLabel) {
        link.setAttribute('aria-label', count > 0 ? `${baseLabel}. ${label}` : baseLabel);
      }
    });
  }

  function dispatch(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function syncCheckoutLock() {
    const locked = queuedMutations > 0;
    document.documentElement.toggleAttribute('data-cart-mutating', locked);

    document.querySelectorAll('[data-cart-checkout]').forEach((checkout) => {
      if (locked) checkout.setAttribute('aria-busy', 'true');
      else checkout.removeAttribute('aria-busy');
      checkout.removeAttribute('aria-disabled');
    });
  }

  function normalizeError(payload, fallback) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload?.description) return payload.description;
    if (payload?.message) return payload.message;
    if (payload?.errors && typeof payload.errors === 'string') return payload.errors;
    return fallback;
  }

  async function requestJson(url, options = {}, fallback = copy.updateError || 'De winkelwagen kon niet worden bijgewerkt.') {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) throw new Error(normalizeError(payload, fallback));
    return payload;
  }

  function commit(cart, detail = {}) {
    cartState = cart;
    renderCartCount(cart);
    dispatch('wn:cart:updated', { ...detail, cart });
    return cart;
  }

  function store(cart) {
    cartState = cart;
    renderCartCount(cart);
    return cart;
  }

  function publish(cart, detail = {}) {
    if (!cart || !Array.isArray(cart.items)) throw new Error(copy.updateError || 'De winkelwagenstatus is ongeldig.');
    return commit(cart, detail);
  }

  async function fetchCart() {
    return requestJson(routes.cart, {}, copy.fetchError || 'De winkelwagen kon niet worden opgehaald.');
  }

  async function refresh(options = {}) {
    const { emit = true, source = 'refresh', openDrawer = false } = options;

    if (!refreshPromise) {
      refreshPromise = fetchCart().finally(() => {
        refreshPromise = null;
      });
    }

    const cart = await refreshPromise;
    if (emit) commit(cart, { source, openDrawer });
    else {
      cartState = cart;
      renderCartCount(cart);
    }
    return cart;
  }

  function enqueue(source, task) {
    queuedMutations += 1;
    syncCheckoutLock();

    const run = async () => {
      dispatch('wn:cart:loading', { source, loading: true });
      try {
        return await task();
      } catch (error) {
        dispatch('wn:cart:error', { source, error, message: error.message });
        throw error;
      } finally {
        queuedMutations = Math.max(0, queuedMutations - 1);
        syncCheckoutLock();
        dispatch('wn:cart:loading', { source, loading: false });
      }
    };

    mutationQueue = mutationQueue.catch(() => null).then(run);
    return mutationQueue;
  }

  function add(input, options = {}) {
    const { source = 'add', openDrawer = true, emit = true } = options;

    return enqueue(source, async () => {
      const fetchOptions = {
        method: 'POST'
      };

      if (input instanceof FormData) {
        fetchOptions.body = input;
      } else {
        fetchOptions.headers = { 'Content-Type': 'application/json' };
        fetchOptions.body = JSON.stringify(input);
      }

      const item = await requestJson(routes.add, fetchOptions, copy.addError || 'Toevoegen is niet gelukt.');
      const cart = await fetchCart();
      if (emit) commit(cart, { source, openDrawer, item });
      else store(cart);
      return { cart, item };
    });
  }

  function change(input, options = {}) {
    const { source = 'change', openDrawer = false, emit = true } = options;

    return enqueue(source, async () => {
      const cart = await requestJson(routes.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      }, copy.quantityError || 'Het aantal kon niet worden aangepast.');

      if (emit) commit(cart, { source, openDrawer });
      else store(cart);
      return cart;
    });
  }

  function update(input, options = {}) {
    const { source = 'update', openDrawer = false, emit = true } = options;

    return enqueue(source, async () => {
      const cart = await requestJson(routes.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      }, copy.updateError || 'De winkelwagen kon niet worden bijgewerkt.');

      if (emit) commit(cart, { source, openDrawer });
      else store(cart);
      return cart;
    });
  }

  async function waitForCartToSettle() {
    let queueGuard = 0;
    while (queuedMutations > 0 && queueGuard < 20) {
      const queue = mutationQueue;
      try {
        await queue;
      } catch {
        // Cart errors are surfaced elsewhere. Checkout will use the latest valid cart state.
      }
      queueGuard += 1;
      if (queue === mutationQueue && queuedMutations === 0) break;
    }

    const drawer = document.querySelector('wn-cart-drawer');
    let drawerGuard = 0;
    while (
      drawer &&
      (drawer.refreshing === true || drawer.hasAttribute('aria-busy')) &&
      drawerGuard < 100
    ) {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      drawerGuard += 1;
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  document.addEventListener('wn:cart:updated', (event) => {
    const cart = event.detail?.cart;
    if (!cart || cart === cartState) return;
    cartState = cart;
    renderCartCount(cart);
  });

  window.WNCart = Object.freeze({
    add,
    change,
    update,
    refresh,
    publish,
    settle: waitForCartToSettle,
    getState: () => cartState
  });
})();
